import { CircularProgress, Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { Datasets } from "../../../../../constants/enums"
import {
    EVAC_AC_ONLY_REG,
    EVAC_ADDITIONAL_COMMENTS_REG,
    EVAC_BARCODE_REG,
    EVAC_MANUFACTURER_REG,
    EVAC_MAX_CURRENT_REG,
    EVAC_MODEL_REF_REG,
    EVAC_MODEL_REG,
    EVAC_PHASES_REG,
    EVAC_V2G_CAPABILITY_REG,
    EVAC_V2G_EXPORT_CAPACITY_REG,
    EVAC_V2G_G99_REG,
    EVAC_VOLTAGE_REG,
    evacDeviceFormLabelMapping,
    v2gG99Options,
} from "../../../../../constants/lct/evac/deviceForm"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import useUploadedDeviceAttachments from "../../../../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { booleanToYesNo } from "../../../../../services/middleware/hooks/devices/lct/postBodyGenerators"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    getDisplayName,
    goToPage,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import FormSummary from "../../../../formSummary/formSummary"
import Confirmation from "../../../../popUps/registerDevice/confirmation"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { EVACDeviceFormData } from "../../types/providerTypes"

interface IReviewAndSubmitProps {
    context: Context<IDeviceFormContextValue<EVACDeviceFormData>>
    isAmendment: boolean
}

const ReviewAndSubmitEVACDevice = ({
    context,
    isAmendment,
}: IReviewAndSubmitProps): ReactElement => {
    const {
        formData,
        deviceRef,
        currentIndex,
        setCurrentIndex,
        onSubmit,
        submitLoading,
        submitSuccessful,
        canEditManufacturer,
        attachmentUUID,
        saveDraft,
        saveDraftLoading,
        errors: contextErrors,
    } = useContext(context)

    const {
        uploadedAttachments,
        error: listAttachmentsError,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
        error: uploadedAttachmentsError,
    } = useUploadedDeviceAttachments(
        Datasets.EVAC,
        deviceRef,
        attachmentUUID,
        isAmendment
    )
    const { manufacturerMappings } = useFilterReferenceMappings()

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !uploadedAttachmentsError &&
        !!uploadedAttachments

    const showPageEdit = !submitLoading

    const goToAttachmentsPage = (): void =>
        goToPage(2, canEditManufacturer, setCurrentIndex)

    const goToCoreEVACDeviceDetailsPage = (): void =>
        goToPage(1, canEditManufacturer, setCurrentIndex)

    const goToCoreEVACManufacturerPage = (): void =>
        goToPage(0, canEditManufacturer, setCurrentIndex)

    const onClickBack = (): void => setCurrentIndex(currentIndex - 1)

    const showManufacturerEdit = canEditManufacturer && !submitLoading

    const attachmentMappings: Record<
        string,
        string | number | boolean | undefined
    > = {}

    if (uploadedAttachmentsAreValid) {
        uploadedAttachments.forEach((summary) => {
            attachmentMappings[summary.fileName] = summary.description
        })
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    if (listAttachmentsError) {
        return <ErrorDisplay error={listAttachmentsError} />
    }

    return (
        <>
            <Confirmation
                isOpen={submitSuccessful}
                deviceRef={deviceRef}
                dataset={Datasets.EVAC}
            />
            <Stack spacing={GRID_SPACING} paddingTop={GRID_SPACING}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="Please check through your details prior to submitting"
                    index={currentIndex}
                    headerTypography={{
                        color: "text.primary",
                    }}
                />
                <FormSummary
                    summaries={[
                        {
                            title: "Manufacturer",
                            mappings: {
                                [evacDeviceFormLabelMapping[
                                    EVAC_MANUFACTURER_REG
                                ]]: formData.manufacturerId
                                    ? getDisplayName(
                                          manufacturerMappings,
                                          Number(formData.manufacturerId)
                                      )
                                    : "Not found",
                            },
                            onEdit: showManufacturerEdit
                                ? goToCoreEVACManufacturerPage
                                : undefined,
                            omit: !canEditManufacturer,
                        },
                        {
                            title: "Core EVAC Device Details",
                            mappings: {
                                [evacDeviceFormLabelMapping[EVAC_MODEL_REG]]:
                                    formData.deviceModel,
                                [evacDeviceFormLabelMapping[
                                    EVAC_MODEL_REF_REG
                                ]]: formData.deviceModelRef,
                                [evacDeviceFormLabelMapping[EVAC_BARCODE_REG]]:
                                    formData.barcodeNumber,
                                [evacDeviceFormLabelMapping[EVAC_PHASES_REG]]:
                                    formData.phases ?? undefined,
                                [evacDeviceFormLabelMapping[EVAC_VOLTAGE_REG]]:
                                    formData.voltage ?? undefined,
                                [evacDeviceFormLabelMapping[
                                    EVAC_MAX_CURRENT_REG
                                ]]: formData.maxCurrentAmpPerPhase ?? undefined,
                                [evacDeviceFormLabelMapping[EVAC_AC_ONLY_REG]]:
                                    typeof formData.acOnlyOutput === "boolean"
                                        ? booleanToYesNo(formData.acOnlyOutput)
                                        : undefined,
                                [evacDeviceFormLabelMapping[
                                    EVAC_V2G_CAPABILITY_REG
                                ]]:
                                    typeof formData.v2gCapability === "boolean"
                                        ? booleanToYesNo(formData.v2gCapability)
                                        : undefined,
                                [evacDeviceFormLabelMapping[
                                    EVAC_V2G_EXPORT_CAPACITY_REG
                                ]]: formData.v2gExportCapacity ?? undefined,
                                [evacDeviceFormLabelMapping[EVAC_V2G_G99_REG]]:
                                    getDisplayName(
                                        v2gG99Options,
                                        formData.v2gG99gbG999niBoth
                                    ),

                                [evacDeviceFormLabelMapping[
                                    EVAC_ADDITIONAL_COMMENTS_REG
                                ]]: formData.comments,
                            },
                            onEdit: showPageEdit
                                ? goToCoreEVACDeviceDetailsPage
                                : undefined,
                        },
                        {
                            title: `Files - ${
                                uploadedAttachmentsAreValid
                                    ? uploadedAttachments.length
                                    : "Loading"
                            }`,
                            mappings: attachmentMappings,
                            onEdit: showPageEdit
                                ? goToAttachmentsPage
                                : undefined,
                        },
                    ]}
                />
                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={onSubmit}
                    onClickMiddle={() => saveDraft(formData)}
                    nextText="Submit"
                    backText="Back"
                    allDisabled={submitLoading || saveDraftLoading}
                    nextIcon={
                        submitLoading ? (
                            <CircularProgress size={20} />
                        ) : undefined
                    }
                />
            </Stack>
        </>
    )
}

export default ReviewAndSubmitEVACDevice
